import {
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import type { LoanBorrower } from '@/types/LoanApplicationType';
import { ENTITY_TYPES } from './constants';

type Props = {
  local: LoanBorrower;
  disabled: boolean;
  onPatch: (partial: Partial<LoanBorrower>) => void;
};

function clampOwnerCount(value: number | null): number {
  if (value == null || !Number.isFinite(value)) return 0;
  return Math.min(20, Math.max(0, Math.floor(value)));
}

export function BorrowerEntityFields({ local, disabled, onPatch }: Props) {
  const ownerCount = clampOwnerCount(local.entityOwnerCount);
  const percentages = local.entityOwnerPercentages ?? [];

  const setOwnerCount = (raw: string) => {
    const count = raw === '' ? null : clampOwnerCount(Number(raw));
    const nextCount = count ?? 0;
    const nextPercents = [...percentages];
    while (nextPercents.length < nextCount) nextPercents.push(0);
    while (nextPercents.length > nextCount) nextPercents.pop();
    onPatch({
      entityOwnerCount: count,
      entityOwnerPercentages: nextPercents,
    });
  };

  const setOwnerPercent = (index: number, raw: string) => {
    const next = [...percentages];
    while (next.length <= index) next.push(0);
    next[index] = raw === '' ? 0 : Number(raw) || 0;
    onPatch({ entityOwnerPercentages: next });
  };

  const percentTotal = percentages
    .slice(0, ownerCount)
    .reduce((sum, p) => sum + (Number(p) || 0), 0);

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary">
          Entity information
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label="Entity type"
          value={local.entityType ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ entityType: e.target.value })}
        >
          <MenuItem value="">Select…</MenuItem>
          {ENTITY_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Entity name"
          value={local.entityName ?? ''}
          disabled={disabled}
          onChange={(e) => {
            const name = e.target.value;
            onPatch({ entityName: name, entityTitle: name });
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Entity EIN"
          value={local.entityEin ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ entityEin: e.target.value })}
          placeholder="XX-XXXXXXX"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="State of formation"
          value={local.entityState ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ entityState: e.target.value })}
          placeholder="e.g. DE, FL"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Entity created date"
          type="date"
          value={local.entityCreatedDate ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ entityCreatedDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Entity owner count"
          type="number"
          inputProps={{ min: 0, max: 20, step: 1 }}
          value={local.entityOwnerCount ?? ''}
          disabled={disabled}
          onChange={(e) => setOwnerCount(e.target.value)}
        />
      </Grid>
      {ownerCount > 0 &&
        Array.from({ length: ownerCount }, (_, i) => (
          <Grid item xs={12} md={4} key={i}>
            <TextField
              fullWidth
              label={`Owner ${i + 1} ownership %`}
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={percentages[i] ?? ''}
              disabled={disabled}
              onChange={(e) => setOwnerPercent(i, e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
        ))}
      {ownerCount > 0 && (
        <Grid item xs={12}>
          <Typography
            variant="caption"
            color={
              Math.abs(percentTotal - 100) < 0.01
                ? 'text.secondary'
                : 'warning.main'
            }
          >
            Ownership total: {percentTotal.toFixed(2)}%
            {Math.abs(percentTotal - 100) >= 0.01 ? ' — should equal 100%' : ''}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Entity purpose"
          value={local.entityPurpose ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ entityPurpose: e.target.value })}
          multiline
          minRows={2}
          placeholder="e.g. Hold investment real estate"
        />
      </Grid>
    </>
  );
}
